import { useEffect, useState, useCallback } from "react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { loadRegistrationData } from "@/services/registration-data.service";
import { publishProfile } from "@/services/publicProfile.service";
import type { RoleKey } from "./roleProfileSchema";

export function useRoleProfile(role: RoleKey) {
  const { user } = useAuth();
  const [data, setData] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, "users", user.id));
        const userDoc = (snap.exists() ? snap.data() : {}) as any;
        // Fallback hydration: registrationData/{uid}_{role}
        let registration = userDoc.registrationData;
        if (!registration) {
          registration = (await loadRegistrationData(user.id, role)) || undefined;
        }
        const merged = {
          ...userDoc,
          registrationData: registration || {},
        };
        // Flatten nested arrays for profile display
        // specializations[0] and licenses[0] are the primary entries
        const rd = merged.registrationData || {};
        if (Array.isArray(rd.specializations) && rd.specializations.length > 0) {
          if (!rd.specializations[0]) rd.specializations[0] = {};
        }
        if (Array.isArray(rd.licenses) && rd.licenses.length > 0) {
          if (!rd.licenses[0]) rd.licenses[0] = {};
        }
        if (!cancelled) setData({ ...merged, registrationData: rd });
      } catch (err) {
        console.error("[useRoleProfile] load error", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, role]);

  const save = useCallback(async (updates: any) => {
    if (!user?.id) return;
    setSaving(true);
    try {
      await setDoc(
        doc(db, "users", user.id),
        { ...updates, updatedAt: serverTimestamp() },
        { merge: true }
      );
      setData((prev: any) => {
        const currentData = { ...prev, ...updates };

        // Sync to publicProfiles if user has a public role
        const userRoles: string[] = currentData.roles || [];
        const publicRole = userRoles.includes("internal-partners")
          ? "partner"
          : userRoles.includes("consultant")
          ? "consultant"
          : userRoles.includes("broker")
          ? "broker"
          : userRoles.includes("buyer")
          ? "buyer"
          : userRoles.includes("seller")
          ? "seller"
          : null;

        if (publicRole && user?.id) {
          publishProfile(
            user.id,
            publicRole as "broker" | "consultant" | "partner" | "buyer" | "seller",
            currentData,
            currentData.visibility || {}
          ).catch((err) =>
            console.warn("[useRoleProfile] publicProfiles sync:", err)
          );
        }

        return currentData;
      });
    } finally {
      setSaving(false);
    }
  }, [user?.id]);

  return { data, setData, loading, saving, save };
}
